/**
 * ConsumptionCertificate — PF v2 native configuration UI (Wave 2, module 2a).
 *
 * Lifts the presentational markup from the legacy ConsumptionCertificateStep sub-tab
 * bodies (`PurchaseFlow/steps/ConsumptionCertificateStep/**`) but rewrites ALL data
 * wiring onto the PF v2 store + file hub via the module's ops hook + codec + upload
 * controller — no constate provider tree, no 3× sub-context, no `useImperativeHandle`,
 * no legacy controller. F-B1 dies by construction: state lives in `productConfigs`
 * (placed by a synchronous hydrate `set()` before mount), mounting only READS it, and
 * the ONLY persisted-slice write effect is the interaction-gated + value-guarded upload
 * sync (§4b).
 *
 * The four sub-tabs are all kept MOUNTED (visibility-toggled), so the heater/billing
 * CRUD rows keep their stable local ids (C6) + leaf-local mid-edit state across tab
 * switches (legacy kept this in constate; native keeps it in panel `useState`).
 *
 * Dropped vs. the legacy controller (logged as DFs, mirror measurement DF-3 / PoA DF-10):
 *   - nothing on the analytics axis any more: the four `OPENS_*` events are back,
 *     fired on panel SELECTION rather than on a panel's mount, which is the same
 *     act here and the only correct hook for panels that stay mounted.
 *   - `StepWrapper` / the legacy controller shell → this native component owns its layout.
 *
 * ZERO imports from `components/pages/PurchaseFlow/*` (DoD grep gate). Reuses the
 * `purchase_flow:consumption_certificate_step.*` + `additional_information_page.*` +
 * `heating_system` i18n namespaces verbatim (no re-translation for Wave 2).
 */

import React from 'react'
import { useTranslation } from 'react-i18next'

import type { CartItemId } from 'models/purchaseFlowRevamp'
import type { CartMutationHandler } from '../../adapters/bridge'

import { PfSubTabConfigurator, useSubTabs } from '../../components/PfSubTabConfigurator'
import { useConsumptionCertificateConfig } from './useConsumptionCertificateConfig'
import {
  isAdditionalInformationValid,
  isConsumptionInformationValid,
  isHeatingSystemValid,
  isPropertyInformationValid
} from './consumptionCertificate.dto'
import { useConfigPanelAnnouncement } from '../useConfigPanelAnnouncement'
import { PropertyInformationPanel } from './panels/PropertyInformationPanel'
import { HeatingSystemPanel } from './panels/HeatingSystemPanel'
import { ConsumptionInformationPanel } from './panels/ConsumptionInformationPanel'
import { AdditionalInformationPanel } from './panels/AdditionalInformationPanel'

/** The four sub-tabs (order matches legacy `InformationType`). */
export enum InformationType {
  PROPERTY_INFORMATION = 'PROPERTY_INFORMATION',
  HEATING_SYSTEM = 'HEATING_SYSTEM',
  CONSUMPTION_INFORMATION = 'CONSUMPTION_INFORMATION',
  ADDITIONAL_INFORMATION = 'ADDITIONAL_INFORMATION',
}

export interface ConsumptionCertificateProps {
  cartItemId: CartItemId
  /** Accepted for registry signature uniformity — the cert emits no cart mutations. */
  onCartMutation?: CartMutationHandler
}

/**
 * @component ConsumptionCertificate
 * Native consumption-certificate configuration form (4 sub-tabs). Reads/writes
 * `productConfigs` via `useConsumptionCertificateConfig`; uploads go through the PF
 * v2 file hub.
 */
export const ConsumptionCertificate: React.FC<ConsumptionCertificateProps> = ({ cartItemId }) => {
  const { t } = useTranslation(['purchase_flow'])
  const { value } = useConsumptionCertificateConfig(cartItemId)
  const tabs = useSubTabs<InformationType>(InformationType.PROPERTY_INFORMATION)

  useConfigPanelAnnouncement(cartItemId, 'consumption_certificate', tabs.active)

  const page = (type: InformationType, isValid: (dto: typeof value) => boolean, content: React.ReactNode) => ({
    id: type,
    label: t(`purchase_flow:consumption_certificate_step.${type.toLowerCase()}`),
    visited: tabs.visited.has(type),
    isValid: isValid(value),
    content,
  })

  return (
    <PfSubTabConfigurator
      title={t('purchase_flow:consumption_certificate_step.information_fill')}
      description={t('purchase_flow:consumption_certificate_step.information_fill_description')}
      activeId={tabs.active}
      onSelect={tabs.select}
      // Heater and billing rows keep their local ids and mid-edit text across page switches.
      keepMounted
      pages={[
        page(InformationType.PROPERTY_INFORMATION, isPropertyInformationValid, <PropertyInformationPanel cartItemId={cartItemId} />),
        page(InformationType.HEATING_SYSTEM, isHeatingSystemValid, <HeatingSystemPanel cartItemId={cartItemId} />),
        page(InformationType.CONSUMPTION_INFORMATION, isConsumptionInformationValid, <ConsumptionInformationPanel cartItemId={cartItemId} />),
        page(InformationType.ADDITIONAL_INFORMATION, isAdditionalInformationValid, <AdditionalInformationPanel cartItemId={cartItemId} />),
      ]}
    />
  )
}
